import {parseNumber} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {
  BenchlingTypeDef,
  LeafType,
  isBenchlingTypeDefComplex,
  isBenchlingTypeDefNullable,
  isBenchlingTypeDefUnion,
  isBenchlingTypeDefList,
  isBenchlingTypeDefAny,
  isBenchlingTypeLeafType,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import APIUtil from 'util/APIUtil';
import {JSONValue} from 'util/Types';

import {tryDeserializeDatetime, tryDeserializeDate} from './Deserializers';

type ValidatorFunction = (value: JSONValue) => boolean;

export const LEAF_TYPE_TO_VALIDATOR: {[key in LeafType]: ValidatorFunction | null} = {
  [LeafType.BOOLEAN]: (value) => value === true || value === false,
  [LeafType.DATETIME]: (value) => typeof value === 'string' && tryDeserializeDatetime(value).deserialized,
  [LeafType.DATE]: (value) => typeof value === 'string' && tryDeserializeDate(value).deserialized,
  [LeafType.DECIMAL]: (value) =>
    (typeof value === 'string' && parseNumber(value) != null) || typeof value === 'number',
  [LeafType.INTEGER]: (value) => typeof value === 'number' && Number.isInteger(value),
  [LeafType.JSON]: () => true,
  [LeafType.TEXT]: (value) => typeof value === 'string',
  [LeafType.BENCHLING_OBJECT]: null,
  [LeafType.BLOB]: null,
  [LeafType.DROPDOWN_OPTION]: (value) =>
    typeof value === 'string' && APIUtil.isSchemaFieldSelectorOption(value),
  [LeafType.BATCH]: (value) => typeof value === 'string' && APIUtil.isBatch(value),
  [LeafType.BIOENTITY]: (value) => typeof value === 'string' && APIUtil.isBiologicalEntity(value),
  [LeafType.BASIC_FOLDER_ITEM]: (value) => typeof value === 'string' && APIUtil.isBasicFolderItem(value),
  [LeafType.MIXTURE]: (value) => typeof value === 'string' && APIUtil.isMixture(value),
  [LeafType.MOLECULE]: (value) => typeof value === 'string' && APIUtil.isMolecule(value),
  [LeafType.PROTEIN]: (value) => typeof value === 'string' && APIUtil.isProtein(value),
  [LeafType.SEQUENCE]: (value) => typeof value === 'string' && APIUtil.isNucleotideSequence(value),
  [LeafType.ENTRY]: (value) => typeof value === 'string' && APIUtil.isEntry(value),
  [LeafType.PROTOCOL]: (value) => typeof value === 'string' && APIUtil.isProtocol(value),
  [LeafType.REQUEST]: (value) => typeof value === 'string' && APIUtil.isRequest(value),
  [LeafType.RESULT]: null,
  [LeafType.RUN]: null,
  [LeafType.STORABLE]: (value) => typeof value === 'string' && APIUtil.isStorable(value),
  [LeafType.BOX]: (value) => typeof value === 'string' && APIUtil.isBox(value),
  [LeafType.CONTAINER]: (value) => typeof value === 'string' && APIUtil.isContainer(value),
  [LeafType.LOCATION]: (value) => typeof value === 'string' && APIUtil.isLocation(value),
  [LeafType.PLATE]: (value) => typeof value === 'string' && APIUtil.isPlate(value),
  [LeafType.PROCESS]: (value) => typeof value === 'string' && APIUtil.isProcess(value),
  [LeafType.PROCESS_TASK]: (value) => typeof value === 'string' && APIUtil.isProcessTask(value),
  [LeafType.PROCESS_OUTPUT]: (value) => typeof value === 'string' && APIUtil.isProcessOutput(value),
  [LeafType.STUDY]: (value) => typeof value === 'string' && APIUtil.isStudy(value),
  [LeafType.TEAM]: (value) => typeof value === 'string' && APIUtil.isTeam(value),
  [LeafType.TEST_ORDER]: (value) => typeof value === 'string' && APIUtil.isTestOrder(value),
  [LeafType.USER]: (value) => typeof value === 'string' && APIUtil.isEntity(value),
};

function getValidatorFunctionForBaseTypes(typeDef: BenchlingTypeDef): ValidatorFunction {
  const baseTypes = isBenchlingTypeDefComplex(typeDef) ? typeDef.getBaseTypes() : [typeDef];
  const validators = baseTypes.map((baseType) => {
    if (isBenchlingTypeLeafType(baseType)) {
      const leafTypeValidator = LEAF_TYPE_TO_VALIDATOR[baseType];
      assert(leafTypeValidator != null, 'Unexpected leaf type', {leafType: baseType});
      return (value: JSONValue): boolean => leafTypeValidator(value);
    } else if (isBenchlingTypeDefComplex(baseType)) {
      return (value: JSONValue): boolean => isValidBenchlingTypeValue(value, baseType);
    } else {
      assert(isBenchlingTypeDefAny(baseType), 'Expected type to be AnyType');
      return (): boolean => true;
    }
  });

  assert(
    validators.length === 1 || isBenchlingTypeDefUnion(typeDef),
    'TypeDef does not support multiple baseTypes',
    {typeDef}
  );

  return (value: JSONValue): boolean => validators.some((validator) => validator(value));
}

/**
 * Validate that a `JSONValue` is a valid value for a given `BenchlingTypeDef`.
 *
 * For api identifiers we are validating that the identifier is correctly formed for the `BenchlingTypeDef`, not
 * that it exists and is viewable. The function supports validating arbitrarily compounded `BenchlingTypeDef`s
 * as long as all of the base `LeafType`s are supported.
 *
 * Currently not all LeafTypes are supported, and those that are not supported have null in the
 * LEAF_TYPE_TO_VALIDATOR enum.
 *
 * @example
 *   isValidBenchlingTypeValue('seq_1234abcd', LeafType.BIOENTITY)
 *   => true
 *
 *   isValidBenchlingTypeValue(['seq_1234abcd', 'seq_2345abcd'], new List(LeafType.BIOENTITY))
 *   => true
 */
export default function isValidBenchlingTypeValue(value: JSONValue, typeDef: BenchlingTypeDef): boolean {
  const baseTypesValidator = getValidatorFunctionForBaseTypes(typeDef);
  const validatorFn = (value: JSONValue): boolean =>
    (isBenchlingTypeDefNullable(typeDef) && value == null) || baseTypesValidator(value);

  if (isBenchlingTypeDefList(typeDef)) {
    return Array.isArray(value) && value.every((v) => validatorFn(v));
  }

  return validatorFn(value);
}
